<template>
  <div class="vuejs-browse-results-container">
    <div
      v-if="deals && deals.length > 0"
      :class="gridClasses"
    >
      <slot
        v-for="(deal, index) in deals"
        :id="index"
      >
        <deal-impression :id="deal.id">
          <div
            :key="deal.id"
            class="appsumo-deal-tile"
            data-testid="deal-tile"
            :slug="deal.slug"
            :media_url="deal.media_url"
          >
            <SKUCard
              v-if="isNewSkuCard"
              class="new-card-design"
              :deal="deal"
              :sku-variant="skuVariant"
            />
            <deal-card-component
              v-else
              class="original-sku-card-design"
              :deal="deal"
              :title="deal.slug"
            />
          </div>
        </deal-impression>
      </slot>
    </div>
    <div
      v-else
      class="empty-deals"
    >
      <p>There are no deals for your current selection</p>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';

import { BROWSE_SKU_CARD_EXPERIMENT_NAME, PLACEHOLDER_IMG } from '@/constants/global';

import SKUCard from '../skucard/SKUCard';
import DealCardComponent from '../common/DealCardComponent';
import DealImpression from '../common/DealImpression';

export default {
  name: 'BrowseResults',

  components: {
    DealCardComponent,
    DealImpression,
    SKUCard,
  },

  props: {
    deals: {
      type: Array,
      required: true,
    },
    isFilterable: {
      type: Boolean,
      required: false,
      default: false,
    },
    session: {
      type: Object,
      required: true,
    },
    skuVariant: {
      type: String,
      required: false,
      default: 'disabled',
    },
  },
  data() {
    return {
      placeholderImg: PLACEHOLDER_IMG,
    };
  },

  computed: {
    gridClasses() {
      return {
        'appsumo-css-grid-container': !this.isNewSkuCard,
        'appsumo-css-grid-container-sku-card': this.isNewSkuCard,
        'justify-content-around': this.deals.length >= 5,
      };
    },
    isNewSkuCard() {
      return this.skuVariant !== 'disabled';
    },
  },

  methods: {
    get,
    selectFilter(filter) {
      this.$emit('filterSelect', filter);
    },
    intersected(dealId) {
      this.$impressions.add(dealId);
    },
  },

  // Parameters is an object with all the component props
  serverCacheKey: ({ session, deals = [], skuVariant }) => {
    // generate server cache key based off of deal data that may influence markup
    const keys = deals.map((deal) => [
      deal.id,
        deal.dates?.start_date,
        deal.dates?.end_date,
        deal.buy_button?.is_last_call,
        deal.participation?.join('!'),
        deal.browse_deal_status,
        `${BROWSE_SKU_CARD_EXPERIMENT_NAME}!${skuVariant}`,
    ]);

    // if the user is logged in these *may* influence markup now or in the future
    if (session.user) {
      keys.push(session.user.has_plus);
      keys.push(session.user.has_briefcase);
      keys.push(session.user.is_betaling);
      keys.push(session.user.is_staff);
    }

    // campaigns can influence markup on deal banners, etc
    if (session.campaigns) {
      keys.push(session.campaigns.join('!'));
    }

    return `BrowseResults::${keys.filter(Boolean).join(',')}`;
  },
};
</script>

<style scoped lang="scss">
@import '~/styles/utils/breakpoints';

.vuejs-browse-results-container {
  margin-bottom: 30px;
}

.empty-deals {
  width: 100%;
}

.appsumo-deal-tile {
  max-width: 450px;
  font-size: 12px;
  padding: 0;
  position: relative;
  box-shadow: none;
}

.appsumo-css-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 20px;
}

.appsumo-css-grid-container-sku-card {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  grid-gap: 15px;

  .appsumo-deal-tile {
    margin-bottom: 1rem;
  }

  .new-card-design {
    height: 100%;
  }
}

@include media-breakpoint-up(md) {
  .appsumo-css-grid-container-sku-card {
    grid-gap: 20px;
    grid-template-columns: repeat(auto-fill, minmax(215px, 1fr));

    .appsumo-deal-tile {
      margin-bottom: 0;
    }
  }
}

@include media-breakpoint-up(xl) {
  .appsumo-css-grid-container-sku-card {
    grid-gap: 20px;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }
}

@media (max-width: 950px) {
  .appsumo-css-grid-container {
    grid-gap: 48px;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }

  .appsumo-deal-tile {
    width: 100%;
  }
}

@media (min-width: 1750px) {
  .appsumo-css-grid-container {
    grid-template-columns: repeat(4, minmax(300px, 500px));
  }

  .vuejs-browse-page.full-width .appsumo-css-grid-container {
    grid-template-columns: repeat(5, minmax(300px, 500px));
  }

  .appsumo-deal-tile {
    max-width: 500px;
    width: 100%;
  }
}

@media (min-width: 1775px) {
  .appsumo-css-grid-container-sku-card {
    grid-gap: 32px;
  }
}

/* Overwrite: Navbar styles to allow nav-bar to fill entire page width */
header .navbar.fixed-top .container {
  max-width: 2000px;
  margin: auto;
  padding: 0 30px;
}

hr {
  border-top: 1px solid #eee;
}
</style>
